import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Star, TrendingUp } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';
import axios from 'axios';
import { API } from '@/api';


export default function Cart() {
  const { items, removeItem, updateQuantity, total, clearCart } = useCart();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loyaltyInfo, setLoyaltyInfo] = React.useState(null);
  const text = (es, en) => pickLocalizedText(lang, es, en);

  // Cargar info de fidelidad para vista previa de puntos
  React.useEffect(() => {
    if (user) {
      axios.get(`${API}/loyalty/balance`).then(r => {
        setLoyaltyInfo(r.data);
      }).catch(() => {});
    }
  }, [user]);

  // Calcular puntos que ganará el usuario con esta compra
  const calculatePointsPreview = () => {
    if (!loyaltyInfo) return { basePoints: 0, bonusPoints: 0, totalPoints: 0 };
    const pointsPerEuro = loyaltyInfo.points_per_euro || 10;
    const multiplier = loyaltyInfo.level?.multiplier || 1;
    const basePoints = Math.floor(total * pointsPerEuro);
    const totalPoints = Math.floor(basePoints * multiplier);
    const bonusPoints = totalPoints - basePoints;
    return { basePoints, bonusPoints, totalPoints };
  };

  const pointsPreview = calculatePointsPreview();

  const handleCheckout = async () => {
    if (!user) { navigate('/auth', { state: { from: '/cart' } }); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || text('Error al procesar el pago', 'Payment processing error'));
    }
    setLoading(false);
  };

  if (items.length === 0) return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center page-enter">
      <ShoppingBag size={64} className="mx-auto text-muted-foreground opacity-30 mb-6" />
      <h2 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cart.empty')}</h2>
      <Link to="/store" className="btn-primary px-8 py-3 inline-block mt-4" data-testid="continue-shopping-empty">{t('cart.continue_shopping')}</Link>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 page-enter">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/store" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cart.title')}</h1>
        <span className="ml-auto text-muted-foreground text-sm">{items.reduce((s, i) => s + i.quantity, 0)} {text('artículos', 'items')}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 space-y-4">
          {items.map(item => (
            <div key={item.product_id} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4" data-testid={`cart-item-${item.product_id}`}>
              <img src={item.image || 'https://via.placeholder.com/80'} alt={item.name}
                className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-foreground font-semibold text-sm truncate">{getLocalizedField(item, 'name', lang, item.name)}</h3>
                <p className="text-[#10B981] font-bold">{item.price?.toFixed(2)}€</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                  className="w-8 h-8 rounded-lg bg-[#1A1A24] flex items-center justify-center hover:bg-white/10 transition-colors"
                  data-testid={`qty-minus-${item.product_id}`}><Minus size={14} /></button>
                <span className="text-foreground font-bold w-6 text-center" data-testid={`qty-${item.product_id}`}>{item.quantity}</span>
                <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                  className="w-8 h-8 rounded-lg bg-[#1A1A24] flex items-center justify-center hover:bg-white/10 transition-colors"
                  data-testid={`qty-plus-${item.product_id}`}><Plus size={14} /></button>
              </div>
              <p className="text-foreground font-bold w-16 text-right">{(item.price * item.quantity).toFixed(2)}€</p>
              <button onClick={() => removeItem(item.product_id)}
                className="p-2 rounded-lg text-[#EF4444] hover:bg-red-500/10 transition-colors"
                data-testid={`remove-${item.product_id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-card border border-border rounded-2xl p-6 h-fit">
          <h3 className="text-foreground font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{text('Resumen', 'Summary')}</h3>
          <div className="space-y-2 mb-4">
            {items.map(i => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-muted-foreground truncate max-w-[140px]">{getLocalizedField(i, 'name', lang, i.name)} x{i.quantity}</span>
                <span className="text-foreground">{(i.price * i.quantity).toFixed(2)}€</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-4 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-foreground font-bold">{t('cart.total')}</span>
              <span className="text-[#10B981] text-xl font-black" data-testid="cart-total">{total.toFixed(2)}€</span>
            </div>
          </div>
          
          {/* Loyalty Points Preview */}
          {user && loyaltyInfo && pointsPreview.totalPoints > 0 && (
            <div className="mb-4 p-3 bg-gradient-to-r from-[#C9A962]/10 to-[#D4B87A]/5 border border-[#C9A962]/20 rounded-xl" data-testid="loyalty-preview">
              <div className="flex items-center gap-2 mb-2">
                <Star size={14} className="text-[#C9A962]" />
                <span className="text-[#C9A962] text-xs font-semibold">
                  {text('Ganarás con esta compra', 'You\'ll earn from this purchase')}
                </span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-[#C9A962] text-2xl font-black">+{pointsPreview.totalPoints}</span>
                <span className="text-[#A8B5AE] text-sm mb-1">{t('loyalty.points')}</span>
              </div>
              {pointsPreview.bonusPoints > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp size={10} className="text-[#2D8B70]" />
                  <span className="text-[#2D8B70] text-xs">
                    +{pointsPreview.bonusPoints} {text('bonus', 'bonus')} (×{loyaltyInfo.level?.multiplier})
                  </span>
                </div>
              )}
              <div className="mt-2 text-[#A8B5AE] text-xs">
                {text('Nivel actual:', 'Current level:')} <span className="font-semibold" style={{ color: loyaltyInfo.level?.color }}>{loyaltyInfo.level?.level}</span>
              </div>
            </div>
          )}
          
          {!user && (
            <div className="mb-4 p-3 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-xl">
              <div className="flex items-center gap-2">
                <Star size={14} className="text-[#C9A962]" />
                <span className="text-[#A8B5AE] text-xs">
                  {text('Inicia sesión para ganar puntos de fidelidad', 'Log in to earn loyalty points')}
                </span>
              </div>
            </div>
          )}

          {error && <p className="text-[#EF4444] text-sm bg-red-500/10 px-3 py-2 rounded-lg mb-4" data-testid="checkout-error">{error}</p>}
          <button onClick={handleCheckout} disabled={loading}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
            data-testid="checkout-btn">
            {loading ? <span className="w-4 h-4 border-2 border-[#0A0A0E]/30 border-t-[#0A0A0E] rounded-full animate-spin"></span> : null}
            {!user ? t('cart.login_required') : t('cart.checkout')}
          </button>
          <Link to="/store" className="block text-center text-muted-foreground text-sm mt-3 hover:text-foreground transition-colors" data-testid="continue-shopping">{t('cart.continue_shopping')}</Link>
        </div>
      </div>
    </div>
  );
}
